import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImageViewer from 'react-simple-image-viewer';
import { Autocomplete, Box, Button, Grid, Stack, SwipeableDrawer, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { dispatch, useSelector } from '../../../../../redux/store';
import { addUtilityBillPayment, updateUtilityBillPaymentDocuments } from '../../../../../redux/slices/financeRedux/utilityBillPaymentRedux';
import { BANK_PAYMENT_TYPES } from '../../../../../utils/constants';
import { CASH_LEDGER_ID } from '../../../../../config';
import { fCurrency } from '../../../../../utils/formatNumber';
import { handleNumber, safeJSONParse, utcMoment } from '../../../../../utils/common';
import { imageUpload } from '../../../../../inteceptor';
import CashFlowImageUploader, { isImageDocument } from '../../cashflow/components/cashflowImageUploader';

const normalizeDocuments = (linkedDocuments) => {
  const parsedDocuments = safeJSONParse(linkedDocuments);
  if (Array.isArray(parsedDocuments)) {
    return parsedDocuments
      .map((value) => {
        if (typeof value === 'string') return value;
        return value?.url;
      })
      .filter((value) => value);
  }
  if (Array.isArray(linkedDocuments)) return linkedDocuments.filter((value) => value);
  return [];
};

const getInputDate = (value) => (value ? utcMoment(value).format('YYYY-MM-DD') : '');

const getUtilityBillOptionLabel = (bill) =>
  `UB-${bill?.id} (${getInputDate(bill?.billDate)}) - ${bill?.utilitySupplierName || '-'} - Bill Value: ${fCurrency(bill?.totalAmount)} - Paid: ${fCurrency(
    bill?.paidAmount
  )} - Available: ${fCurrency(bill?.outstandingAmount)}`;

export default function UtilityBillPaymentDrawer({ isAdd, isModelOpen, setIsModelOpen, selectedDataObj, setSelectedDataObj }) {
  const { utilityBillsToBePaid } = useSelector((state) => state.utilityBillPayment);
  const { cashFlowSubCategories: cashFlowSubCategoriesList } = useSelector((state) => state.cashFlow);
  const { selectedLocation } = useSelector((state) => state.location);
  const { loggedInUser } = useSelector((state) => state.user);

  const [files, setFiles] = useState([]);
  const [documentGallery, setDocumentGallery] = useState([]);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const imageGallery = documentGallery?.filter(isImageDocument) || [];

  const paymentTypeList = useMemo(() => cashFlowSubCategoriesList?.filter((value) => Number(value?.mainCategoryId) === 8) || [], [cashFlowSubCategoriesList]);
  const selectedBill = useMemo(
    () => utilityBillsToBePaid?.find((value) => Number(value?.id) === Number(selectedDataObj?.utilityBillId)),
    [selectedDataObj?.utilityBillId, utilityBillsToBePaid]
  );
  const selectedCashbookLedger = useMemo(
    () => paymentTypeList?.find((value) => Number(value?.id) === Number(selectedDataObj?.cashbookLedgerId)) || null,
    [paymentTypeList, selectedDataObj?.cashbookLedgerId]
  );
  const selectedBankPaymentType = useMemo(
    () => BANK_PAYMENT_TYPES?.find((value) => Number(value?.id) === Number(selectedDataObj?.bankPaymentType)) || null,
    [selectedDataObj?.bankPaymentType]
  );
  const outstandingAmount = handleNumber(selectedBill?.outstandingAmount || selectedDataObj?.outstandingAmount);

  useEffect(() => {
    if (!isModelOpen) return;

    setFiles([]);
    setIsViewerOpen(false);
    setCurrentImage(0);
    setDocumentGallery(isAdd ? [] : normalizeDocuments(selectedDataObj?.linkedDocuments));
  }, [isModelOpen, isAdd, selectedDataObj?.id, selectedDataObj?.linkedDocuments]);

  const updateSelectedObject = (key, value) => {
    setSelectedDataObj((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateBeforeSubmit = () => {
    if (isAdd && !selectedDataObj.utilityBillId) {
      toast.error('Utility bill is required!');
      return false;
    }
    if (isAdd && !selectedDataObj.cashbookLedgerId) {
      toast.error('Cashbook ledger is required!');
      return false;
    }
    if (isAdd && !selectedDataObj.referenceNote?.trim()) {
      toast.error('Reference note is required!');
      return false;
    }
    if (isAdd && !selectedDataObj.paymentDate) {
      toast.error('Payment date is required!');
      return false;
    }
    if (isAdd && !selectedDataObj.scheduledDate) {
      toast.error('Scheduled date is required!');
      return false;
    }
    if (isAdd && (!handleNumber(selectedDataObj.amount) || handleNumber(selectedDataObj.amount) <= 0)) {
      toast.error('Amount must be greater than zero!');
      return false;
    }
    if (isAdd && outstandingAmount > 0 && handleNumber(selectedDataObj.amount) > outstandingAmount) {
      toast.error('Amount cannot exceed available outstanding amount!');
      return false;
    }
    if (!documentGallery.length) {
      toast.error('Related payment documents need to be uploaded first!');
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateBeforeSubmit()) return;

    const payload = {
      ...selectedDataObj,
      locationId: selectedLocation?.id,
      amount: handleNumber(selectedDataObj.amount),
      linkedDocuments: documentGallery,
    };

    dispatch(isAdd ? addUtilityBillPayment(payload) : updateUtilityBillPaymentDocuments(payload));
  };

  const uploadFiles = (file) =>
    new Promise((resolve, reject) => {
      imageUpload(
        file,
        `Finance/UtilityBillPayment/Loc_${selectedLocation?.id}/User_${loggedInUser?.id}`,
        (data) => {
          if (data?.status) {
            resolve(data);
          } else {
            reject(new Error('Upload failed'));
          }
        },
        (errorMessage) => {
          reject(new Error(errorMessage));
        }
      );
    });

  const handleImageUpload = async () => {
    if (!files.length) {
      toast.warning('Please select at least one document to upload.');
      return;
    }

    setIsUploading(true);
    for (const file of files) {
      try {
        setFiles((value) => value.map((fileValue) => (file?.id === fileValue?.id ? { ...fileValue, isUploading: true } : fileValue)));
        const data = await uploadFiles(file);
        setFiles((value) => value.filter((fileValue) => file?.id !== fileValue?.id));
        if (data?.status) {
          setDocumentGallery((prevState) => [...prevState, data?.url]);
        }
      } catch (error) {
        setFiles((value) => value.map((fileValue) => (file?.id === fileValue?.id ? { ...fileValue, isUploading: false } : fileValue)));
      }
    }
    setIsUploading(false);
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  return (
    <SwipeableDrawer
      anchor='top'
      open={isModelOpen}
      onOpen={() => setIsModelOpen(true)}
      onClose={() => setIsModelOpen(false)}
      PaperProps={{
        sx: {
          width: '90%',
          height: '90%',
          marginLeft: '5%',
          marginTop: '3%',
          borderRadius: '10px',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant='h5'>{isAdd ? 'Utility Bill Payment' : 'Update Payment Documents'}</Typography>
      </Box>
      <Grid container spacing={1}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3} sx={{ p: 3 }}>
            <Autocomplete
              onChange={(event, newValue) => {
                if (isAdd) {
                  const bill = utilityBillsToBePaid.find((value) => Number(value?.id) === Number(newValue?.value));
                  setSelectedDataObj((prev) => ({
                    ...prev,
                    utilityBillId: bill?.id || null,
                    outstandingAmount: bill?.outstandingAmount || 0,
                    amount: bill?.outstandingAmount || '',
                  }));
                }
              }}
              options={(utilityBillsToBePaid || []).map((value) => ({
                label: getUtilityBillOptionLabel(value),
                value: value.id,
              }))}
              value={
                selectedDataObj.utilityBillId
                  ? {
                      label: selectedBill ? getUtilityBillOptionLabel(selectedBill) : `UB-${selectedDataObj.utilityBillId}`,
                      value: selectedDataObj.utilityBillId,
                    }
                  : null
              }
              renderInput={(params) => <TextField label='Select Utility Bill' {...params} />}
              disabled={!isAdd}
            />

            {selectedBill && (
              <Box>
                <Typography variant='body2' color='text.secondary'>
                  Account: {selectedBill?.billingAccountName || '-'} {selectedBill?.meterNumber ? `(${selectedBill.meterNumber})` : ''}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Bill Value: {fCurrency(selectedBill?.totalAmount)} | Paid Amount: {fCurrency(selectedBill?.paidAmount)}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Available Outstanding Amount: {fCurrency(selectedBill?.outstandingAmount)}
                </Typography>
              </Box>
            )}

            <Autocomplete
              onChange={(event, newValue) => {
                if (isAdd) updateSelectedObject('cashbookLedgerId', newValue?.id || null);
              }}
              options={paymentTypeList}
              value={selectedCashbookLedger}
              renderInput={(params) => <TextField label='Select Cashbook Ledger' {...params} />}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
              disabled={!isAdd}
            />

            {selectedDataObj.cashbookLedgerId !== CASH_LEDGER_ID && selectedDataObj.cashbookLedgerId !== null && (
              <Autocomplete
                onChange={(event, newValue) => {
                  if (isAdd) updateSelectedObject('bankPaymentType', newValue?.id || null);
                }}
                options={BANK_PAYMENT_TYPES}
                value={selectedBankPaymentType}
                renderInput={(params) => <TextField label='Select Bank Payment Type' {...params} />}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                disabled={!isAdd}
              />
            )}

            <TextField
              fullWidth
              label='Reference Note'
              value={selectedDataObj?.referenceNote || ''}
              onChange={(e) => updateSelectedObject('referenceNote', e.target.value)}
              disabled={!isAdd}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Payment Date'
                  type='date'
                  value={getInputDate(selectedDataObj?.paymentDate)}
                  onChange={(e) => updateSelectedObject('paymentDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!isAdd}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Scheduled Date'
                  type='date'
                  value={getInputDate(selectedDataObj?.scheduledDate)}
                  onChange={(e) => updateSelectedObject('scheduledDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!isAdd}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              type='number'
              label='Amount'
              value={selectedDataObj?.amount || ''}
              onChange={(e) => updateSelectedObject('amount', e.target.value)}
              inputProps={{ min: 0, max: outstandingAmount || undefined, step: '0.01' }}
              disabled={!isAdd}
            />

            <LoadingButton variant='contained' size='large' onClick={handleSubmit}>
              {isAdd ? 'Add Record' : 'Save Documents'}
            </LoadingButton>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <CashFlowImageUploader
            files={files}
            setFiles={setFiles}
            handleImageUpload={handleImageUpload}
            documentGallery={documentGallery}
            openImageViewer={openImageViewer}
            isUploading={isUploading}
          />
          {documentGallery.length > 0 && (
            <Button color='error' variant='outlined' sx={{ mt: 2, ml: 3 }} onClick={() => setDocumentGallery([])}>
              Remove Uploaded Documents
            </Button>
          )}
          {isViewerOpen && (
            <ImageViewer
              src={imageGallery}
              currentIndex={currentImage}
              disableScroll={false}
              closeOnClickOutside={true}
              onClose={() => {
                setCurrentImage(0);
                setIsViewerOpen(false);
              }}
            />
          )}
        </Grid>
      </Grid>
    </SwipeableDrawer>
  );
}
